import { UserProfile } from '../types';

export type SettingsTab =
  | 'kontoinformation'
  | 'design'
  | 'brugere'
  | 'grupper'
  | 'skabeloner'
  | 'templates'
  | 'formularer'
  | 'abonnement'
  | 'faktura'
  | 'emails'
  | 'integrationer'
  | 'integrations';

export const VALID_SETTINGS_TABS: SettingsTab[] = [
  'kontoinformation',
  'design',
  'brugere',
  'grupper',
  'skabeloner',
  'templates',
  'formularer',
  'abonnement',
  'faktura',
  'emails',
  'integrationer',
  'integrations',
];

/**
 * Parses settings tab from a given path or window.location
 */
export function parseSettingsTab(pathStr?: string): SettingsTab {
  const checkTab = (val: string | null | undefined): SettingsTab | null => {
    if (!val) return null;
    const normalized = val.replace('#', '').trim().toLowerCase() as SettingsTab;
    if (normalized === 'integrations') return 'integrationer';
    if (normalized === 'templates') return 'skabeloner';
    if (VALID_SETTINGS_TABS.includes(normalized)) {
      return normalized;
    }
    return null;
  };

  if (pathStr) {
    try {
      const urlObj = new URL(pathStr, 'http://localhost');
      const tabParam = checkTab(urlObj.searchParams.get('tab'));
      if (tabParam) return tabParam;

      const hashStr = checkTab(urlObj.hash);
      if (hashStr) return hashStr;
    } catch {
      // fallback manual parsing
    }

    const searchPart = pathStr.includes('?') ? pathStr.split('?')[1] : '';
    const queryPart = searchPart.split('#')[0];
    const params = new URLSearchParams(queryPart);
    const tabParam = checkTab(params.get('tab'));
    if (tabParam) return tabParam;

    const hashPart = pathStr.includes('#') ? pathStr.split('#')[1].split('?')[0] : '';
    const hashTab = checkTab(hashPart);
    if (hashTab) return hashTab;
  }

  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const tabParam = checkTab(params.get('tab'));
    if (tabParam) return tabParam;

    const hashStr = checkTab(window.location.hash);
    if (hashStr) return hashStr;
  }

  return 'kontoinformation';
}

export type AppView =
  | 'signature-room'
  | 'public-form'
  | 'betingelser'
  | 'privatlivspolitik'
  | 'kontakt-os'
  | 'funktioner'
  | 'priser'
  | 'brancher'
  | 'branche-detail'
  | 'integrationer'
  | 'integration-detail'
  | 'artikler'
  | 'artikel-detail'
  | 'kvikid'
  | 'kviksend'
  | 'kviksign'
  | 'kvikform'
  | 'landing'
  | 'login'
  | 'set-password'
  | 'reset-password'
  | 'dashboard'
  | 'transactions'
  | 'files'
  | 'drafts'
  | 'send'
  | 'superadmin'
  | 'profile'
  | 'settings'
  | 'mitid-verify'
  | 'mitid-log';

interface ResolveViewParams {
  currentPath: string;
  currentUser: UserProfile | null;
  hasSigningScope: boolean;
  hasFilesScope?: boolean;
  isSignatureRoom: boolean;
}

/**
 * Resolves the active application view based on URL path, user session, and permission scopes.
 */
export function resolveAppView({
  currentPath,
  currentUser,
  hasSigningScope,
  hasFilesScope,
  isSignatureRoom,
}: ResolveViewParams): AppView {
  const rawPath = currentPath.split('?')[0].split('#')[0].trim();
  let pathname = (rawPath.replace(/\/+$/, '') || '/').toLowerCase();

  // Strip international language prefixes for public view resolution (/dk, /da, /eng, /en, /sv, /se)
  if (pathname.startsWith('/dk/') || pathname.startsWith('/da/')) {
    pathname = pathname.substring(3);
  } else if (pathname === '/dk' || pathname === '/da') {
    pathname = '/';
  } else if (pathname.startsWith('/eng/')) {
    pathname = pathname.substring(4);
  } else if (pathname.startsWith('/en/')) {
    pathname = pathname.substring(3);
  } else if (pathname === '/eng' || pathname === '/en') {
    pathname = '/';
  } else if (pathname.startsWith('/sv/')) {
    pathname = pathname.substring(3);
  } else if (pathname.startsWith('/se/')) {
    pathname = pathname.substring(3);
  } else if (pathname === '/sv' || pathname === '/se') {
    pathname = '/';
  }

  if (
    pathname === '/set-password' ||
    pathname.startsWith('/set-password') ||
    pathname === '/app/set-password' ||
    currentPath.includes('/set-password')
  ) {
    return 'set-password';
  }

  if (
    pathname === '/reset-password' ||
    pathname.startsWith('/reset-password') ||
    pathname === '/app/reset-password' ||
    currentPath.includes('/reset-password')
  ) {
    return 'reset-password';
  }

  if (
    isSignatureRoom ||
    pathname === '/signature-room' ||
    pathname.startsWith('/signature-room/') ||
    pathname.startsWith('/sign/') ||
    pathname.startsWith('/s/') ||
    pathname.startsWith('/signature/')
  ) {
    return 'signature-room';
  }
  if (
    pathname.startsWith('/f/') ||
    pathname.startsWith('/form/') ||
    pathname.startsWith('/forms/') ||
    pathname === '/f' ||
    pathname === '/form' ||
    pathname === '/forms'
  ) {
    return 'public-form';
  }
  if (pathname.startsWith('/terms') || pathname.startsWith('/betingelser')) {
    return 'betingelser';
  }
  if (pathname.startsWith('/privacy') || pathname.startsWith('/privatlivspolitik')) {
    return 'privatlivspolitik';
  }
  if (
    pathname === '/contact' ||
    pathname === '/kontakt-os' ||
    pathname === '/kontakt' ||
    pathname.startsWith('/contact/') ||
    pathname.startsWith('/kontakt-os/') ||
    pathname.startsWith('/kontakt/')
  ) {
    return 'kontakt-os';
  }
  if (pathname === '/features/kviksign' || pathname === '/funktioner/kviksign' || pathname === '/kviksign') {
    return 'kviksign';
  }
  if (pathname === '/features/kviksend' || pathname === '/funktioner/kviksend' || pathname === '/kviksend') {
    return 'kviksend';
  }
  if (pathname === '/features/kvikid' || pathname === '/funktioner/kvikid' || pathname === '/kvikid') {
    return 'kvikid';
  }
  if (pathname === '/features/kvikform' || pathname === '/funktioner/kvikform' || pathname === '/kvikform') {
    return 'kvikform';
  }
  if (
    pathname === '/features' ||
    pathname === '/funktioner' ||
    pathname.startsWith('/features/') ||
    pathname.startsWith('/funktioner/')
  ) {
    return 'funktioner';
  }
  if (pathname === '/articles' || pathname === '/artikler' || pathname === '/viden' || pathname === '/blog') {
    return 'artikler';
  }
  if (
    pathname.startsWith('/articles/') ||
    pathname.startsWith('/artikler/') ||
    pathname.startsWith('/viden/') ||
    pathname.startsWith('/blog/') ||
    pathname === '/kvalificeret-elektronisk-signatur-qes-gyldighed-eu'
  ) {
    return 'artikel-detail';
  }
  if (
    pathname === '/pricing' ||
    pathname === '/priser' ||
    pathname.startsWith('/pricing/') ||
    pathname.startsWith('/priser/')
  ) {
    return 'priser';
  }
  if (
    pathname === '/industries' ||
    pathname === '/brancher' ||
    pathname === '/branche' ||
    pathname === '/sektorer' ||
    pathname === '/branscher'
  ) {
    return 'brancher';
  }
  if (
    pathname.startsWith('/industries/') ||
    pathname.startsWith('/brancher/') ||
    pathname.startsWith('/branche/') ||
    pathname.startsWith('/branscher/')
  ) {
    return 'branche-detail';
  }
  if (
    pathname === '/integrations' ||
    pathname === '/integrationer' ||
    pathname === '/integration'
  ) {
    return 'integrationer';
  }
  if (
    pathname.startsWith('/integrations/') ||
    pathname.startsWith('/integrationer/') ||
    pathname.startsWith('/integration/')
  ) {
    return 'integration-detail';
  }
  if (pathname === '/settings' || pathname === '/app/settings') {
    if (!currentUser) return 'login';
    return 'settings';
  }

  if (pathname === '/') {
    return 'landing';
  }

  if (pathname === '/login' || pathname === '/signup') {
    return currentUser ? 'dashboard' : 'login';
  }

  if (pathname.startsWith('/app')) {
    if (!currentUser) {
      return 'login';
    }

    if (pathname === '/app/sign/transactions' && hasSigningScope) {
      return 'transactions';
    }
    if ((pathname === '/app/sign/files' || pathname === '/app/sign/filer' || pathname.startsWith('/app/sign/files') || pathname.startsWith('/app/sign/filer')) && hasSigningScope) {
      if (hasFilesScope === false) {
        return 'transactions';
      }
      return 'files';
    }
    if (pathname === '/app/sign/drafts' && hasSigningScope) {
      return 'drafts';
    }
    if (pathname === '/app/sign/send' && hasSigningScope) {
      return 'send';
    }
    if (pathname === '/app/superadmin') {
      return 'superadmin';
    }
    if (pathname === '/app/profile') {
      return 'profile';
    }
    if (pathname === '/app/id/mitid-verify' || pathname === '/mitid-verify') {
      return 'mitid-verify';
    }
    if (pathname === '/app/id/mitid-log' || pathname === '/mitid-log') {
      return 'mitid-log';
    }
    if (pathname.startsWith('/app/id/')) {
      return 'dashboard';
    }
    if (pathname === '/app/sign/dashboard' && hasSigningScope) {
      return 'dashboard';
    }

    return 'dashboard';
  }

  return currentUser ? 'dashboard' : 'landing';
}

/**
 * Extracts initial tab for terms & conditions page based on URL hash or query params
 */
export function getTermsInitialTab(): 'betingelser' | 'dpa' | 'privatliv' {
  const isDpaHash = window.location.hash === '#dpa' || window.location.search.includes('tab=dpa');
  const isPrivatlivHash = window.location.hash === '#privatliv' || window.location.search.includes('tab=privatliv');
  if (isDpaHash) return 'dpa';
  if (isPrivatlivHash) return 'privatliv';
  return 'betingelser';
}

/**
 * Normalizes any route to use English path endings
 */
export function normalizeEnglishPath(path: string): string {
  return path
    .replace(/^\/(dk|da|eng|en|sv|se)(\/|$)/, '/')
    .replace(/^\/priser(\/|$)/, '/pricing$1')
    .replace(/^\/brancher(\/|$)/, '/industries$1')
    .replace(/^\/branche(\/|$)/, '/industries$1')
    .replace(/^\/branscher(\/|$)/, '/industries$1')
    .replace(/^\/integrationer(\/|$)/, '/integrations$1')
    .replace(/^\/integration(\/|$)/, '/integrations$1')
    .replace(/^\/funktioner(\/|$)/, '/features$1')
    .replace(/^\/kontakt-os(\/|$)/, '/contact$1')
    .replace(/^\/kontakt(\/|$)/, '/contact$1')
    .replace(/^\/betingelser(\/|$)/, '/terms$1')
    .replace(/^\/privatlivspolitik(\/|$)/, '/privacy$1')
    .replace(/^\/sikkerhed-og-drift(\/|$)/, '/security$1')
    .replace(/^\/artikler(\/|$)/, '/articles$1');
}

/**
 * Returns the localized URL path for a given route and language with all English slug endings
 */
export function getLocalizedPath(path: string, lang: 'da' | 'en' | 'sv'): string {
  // Do not modify internal /app or signing session routes
  if (
    path.startsWith('/app') ||
    path.startsWith('/sign/') ||
    path.startsWith('/s/') ||
    path.startsWith('/signature') ||
    path.startsWith('/f/') ||
    path.startsWith('/form/') ||
    path.startsWith('/forms/') ||
    path === '/set-password' ||
    path === '/reset-password'
  ) {
    return path;
  }

  let clean = normalizeEnglishPath(path);
  if (!clean.startsWith('/')) clean = '/' + clean;

  if (lang === 'da') {
    if (clean === '/' || clean === '') return '/da';
    if (clean === '/industries') return '/da/brancher';
    if (clean.startsWith('/industries/')) return `/da/brancher/${clean.slice(12)}`;
    if (clean === '/integrations') return '/da/integrationer';
    if (clean.startsWith('/integrations/')) return `/da/integrationer/${clean.slice(14)}`;
    return `/da${clean}`;
  }

  if (lang === 'sv') {
    if (clean === '/' || clean === '') return '/sv';
    return `/sv${clean}`;
  }

  // lang === 'en'
  if (clean === '/' || clean === '') return '/en';
  return `/en${clean}`;
}

/**
 * Converts a current URL path to its opposite language equivalent
 */
export function switchLanguagePath(currentPath: string, targetLang: 'da' | 'en' | 'sv'): string {
  return getLocalizedPath(currentPath, targetLang);
}

